import {
  Component,
  DestroyRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, filter, NEVER, Observable, of, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { openConfirmationDialog } from '../../../../../../components/confirmation-popup/confirmation-popup';
import { openSaveWithoutContactsPopup } from './components/save-without-contacts-popup/save-without-contacts-popup';
import { OrganisationsService } from '../../services/organisations.service';
import { OrganisationDetail, OrgDepartmentAssignment, OrgPersonAssignment } from '../../models/organisation-detail';
import { buildSaveRequest } from '../../utils/organisations.util';
import { AssignableParty } from '../../../contacts/components/add-department-contact/models/assignable-party';
import { ContactsService } from '../../../contacts/services/contacts.service';
import { Contact } from '../../../contacts/models/contacts';
import { SupportContactLookupsService } from '../../../contacts/components/add-department-contact/services/support-contact-lookups.service';
import { PeopleSectionComponent } from './components/people-section/people-section.component';
import { DepartmentsSectionComponent } from './components/departments-section/departments-section.component';
import { CustomerPreviewComponent } from './components/customer-preview/customer-preview.component';

@Component({
  selector: 'app-organisation-detail',
  standalone: true,
  imports: [MatIcon, MatProgressSpinner, PeopleSectionComponent, DepartmentsSectionComponent, CustomerPreviewComponent],
  templateUrl: './organisation-detail.component.html',
  styleUrl: './organisation-detail.component.scss'
})
export class OrganisationDetailComponent implements OnInit, OnChanges {
  @Input() partyNumber: string | null = null;
  @Input() newOrgParty: AssignableParty | null = null;
  // Set only alongside newOrgParty — the "Add organisation" popover's own /supportContacts/search
  // request, started in parallel with its unassigned-parties fetch. When present, ngOnInit reuses
  // it instead of firing a fresh request. Opening an existing organisation leaves this null, so
  // that path keeps fetching contacts fresh on its own, unchanged.
  @Input() prefetchedContacts$: Observable<Contact[]> | null = null;
  @Output() back = new EventEmitter<boolean>();

  partyId = 0;
  partyNameValue = '';
  partyNumberValue = '';
  people: OrgPersonAssignment[] = [];
  departments: OrgDepartmentAssignment[] = [];
  isNew = false;
  dirty = false;
  loading = false;
  hasError = false;
  saving = false;

  // Fetched once per page view and shared by both sections below (each still filters it locally
  // by `type`) — avoids People and Departments each firing their own /supportContacts/search for
  // the same unpaged dataset.
  contacts: Contact[] = [];

  // Keyed by Product id, passed to DepartmentsSectionComponent so it can set
  // OrgDepartmentAssignment.product (a name) when adding an unassigned contact here — the
  // Contacts grid resolves its own Type-column icon against the same shared/cached lookups.
  productsById = new Map<number, string>();

  // Set once on load/reset and never mutated in place — discard() and a successful save() both
  // restore/replace it wholesale, so the working arrays can always be compared against it if a
  // richer diff is ever needed later.
  private snapshot: { people: OrgPersonAssignment[]; departments: OrgDepartmentAssignment[] } = {
    people: [],
    departments: []
  };

  private organisationsService = inject(OrganisationsService);
  private contactsService = inject(ContactsService);
  private lookupsService = inject(SupportContactLookupsService);
  private matDialog = inject(MatDialog);
  private matSnackBar = inject(MatSnackBar);
  private destroyRef = inject(DestroyRef);

  ngOnInit() {
    this.lookupsService
      .getLookups()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(lookups => {
        this.productsById = new Map(lookups.products.map(product => [product.id, product.name]));
      });
    if (this.newOrgParty && this.prefetchedContacts$) {
      this.prefetchedContacts$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(contacts => (this.contacts = contacts));
      return;
    }
    this.contactsService
      .getAllContacts()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(response => (this.contacts = response.data));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['partyNumber'] || changes['newOrgParty']) {
      if (this.newOrgParty) {
        this.startNewOrganisation(this.newOrgParty);
      } else if (this.partyNumber) {
        this.loadDetail(this.partyNumber);
      }
    }
  }

  // Guards against the tab/browser being closed outright while dirty — in-app navigation (the
  // breadcrumb, or leaving the /admin/organisations route) is covered by goBack() and the
  // canDeactivate guard respectively, neither of which reaches this event.
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent) {
    if (this.dirty) {
      event.preventDefault();
      event.returnValue = true;
    }
  }

  get organisationParty(): AssignableParty {
    return { id: this.partyId, number: this.partyNumberValue, name: this.partyNameValue };
  }

  markDirty() {
    this.dirty = true;
  }

  addPerson(assignment: OrgPersonAssignment) {
    this.people = [...this.people, assignment];
    this.markDirty();
  }

  removePerson(contactId: number) {
    this.people = this.people.filter(person => person.contactId !== contactId);
    this.markDirty();
  }

  reorderPeople(people: OrgPersonAssignment[]) {
    this.people = people;
    this.markDirty();
  }

  updatePerson(contactId: number, patch: Partial<OrgPersonAssignment>) {
    this.people = this.people.map(person => (person.contactId === contactId ? { ...person, ...patch } : person));
    this.markDirty();
  }

  addDepartment(assignment: OrgDepartmentAssignment) {
    this.departments = [...this.departments, assignment];
    this.markDirty();
  }

  removeDepartment(contactId: number) {
    this.departments = this.departments.filter(department => department.contactId !== contactId);
    this.markDirty();
  }

  reorderDepartments(departments: OrgDepartmentAssignment[]) {
    this.departments = departments;
    this.markDirty();
  }

  goBack() {
    this.confirmDiscardIfDirty(() => this.back.emit(false));
  }

  discard() {
    this.confirmDiscardIfDirty(() => {
      this.people = this.snapshot.people.map(person => ({ ...person }));
      this.departments = this.snapshot.departments.map(department => ({ ...department }));
      this.dirty = false;
    });
  }

  save() {
    if (this.people.length === 0 && this.departments.length === 0) {
      openSaveWithoutContactsPopup(this.matDialog, { organisationName: this.partyNameValue })
        .afterClosed()
        .pipe(
          filter(result => result === 'Confirmed'),
          tap(() => this.doSave()),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe();
      return;
    }
    this.doSave();
  }

  private doSave() {
    this.saving = true;
    const request = buildSaveRequest(this.people, this.departments);
    this.organisationsService
      .save(this.partyId, request)
      .pipe(
        tap(() => {
          this.saving = false;
          this.dirty = false;
          this.matSnackBar.open('Organisation saved', 'x', { panelClass: 'snackbar-green' });
          this.back.emit(true);
        }),
        catchError((error: HttpErrorResponse) => {
          console.error(error);
          this.saving = false;
          this.matSnackBar.open('Could not save this organisation. Please try again.', 'x');
          return NEVER;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private confirmDiscardIfDirty(action: () => void) {
    if (!this.dirty) {
      action();
      return;
    }
    openConfirmationDialog(this.matDialog, {
      title: 'Discard changes?',
      message: 'You have unsaved changes on this organisation. Discard them?'
    })
      .afterClosed()
      .pipe(
        filter(result => result === 'Confirmed'),
        tap(action),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private startNewOrganisation(party: AssignableParty) {
    this.partyId = party.id;
    this.partyNumberValue = party.number;
    this.partyNameValue = party.name;
    this.people = [];
    this.departments = [];
    this.isNew = true;
    this.dirty = false;
    this.hasError = false;
    this.loading = false;
    this.snapshot = { people: [], departments: [] };
  }

  private loadDetail(partyNumber: string) {
    this.loading = true;
    this.hasError = false;
    this.isNew = false;
    this.organisationsService
      .getDetail(partyNumber)
      .pipe(
        tap((detail: OrganisationDetail) => {
          this.applyDetail(detail);
          this.loading = false;
        }),
        catchError(error => {
          console.error(error);
          this.hasError = true;
          this.loading = false;
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private applyDetail(detail: OrganisationDetail) {
    this.partyId = detail.partyId;
    this.partyNumberValue = detail.partyNumber;
    this.partyNameValue = detail.partyName;
    this.people = [...detail.people].sort((a, b) => a.sortOrder - b.sortOrder);
    this.departments = [...detail.departments].sort((a, b) => a.sortOrder - b.sortOrder);
    this.dirty = false;
    this.snapshot = {
      people: this.people.map(person => ({ ...person })),
      departments: this.departments.map(department => ({ ...department }))
    };
  }
}
